import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { API_BASE_URL } from "../config";

// Validation schema
const schema = yup.object({
    full_name: yup
        .string()
        .required("Full name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name must be less than 100 characters"),
    email: yup
        .string()
        .required("Email is required")
        .email("Please enter a valid email address"),
    phone: yup
        .string()
        .required("Phone number is required")
        .matches(/^[0-9+\s()-]+$/, "Please enter a valid phone number")
        .min(10, "Phone number must be at least 10 digits"),
    linkedin: yup
        .string()
        .url("Please enter a valid URL")
        .nullable()
        .transform((value) => value === "" ? null : value),
    cover_letter: yup
        .string()
        .max(2000, "Cover letter must be less than 2000 characters"),
    cv_file: yup
        .mixed()
        .required("CV/Resume is required")
        .test("fileSize", "File size must be less than 5MB", (value) => {
            if (!value || !value[0]) return false;
            return value[0].size <= 5 * 1024 * 1024;
        })
        .test("fileType", "Only PDF and Word documents are allowed", (value) => {
            if (!value || !value[0]) return false;
            const validTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
            return validTypes.includes(value[0].type);
        }),
}).required();

const ApplicationModal = ({ isOpen, onClose, job }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [hasApplied, setHasApplied] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
    });

    // Check if user has already applied for this job
    useEffect(() => {
        if (isOpen && job?.id) {
            const appliedJobs = JSON.parse(localStorage.getItem('appliedJobs') || '[]');
            setHasApplied(appliedJobs.includes(job.id));
        }
    }, [isOpen, job?.id]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            reset();
            setSubmitSuccess(false);
            setSubmitError(null);
            setSelectedFile(null);
        }
    }, [isOpen, reset]);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            handleFileSelect(files);
        }
    };

    const handleFileSelect = (files) => {
        const file = files[0];
        setSelectedFile(file);

        // Create a FileList-like object for react-hook-form
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        setValue("cv_file", dataTransfer.files, { shouldValidate: true });
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileSelect(e.target.files);
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setValue("cv_file", null, { shouldValidate: true });
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append("career_id", job.id);
            formDataToSend.append("job_title", job.title);
            formDataToSend.append("full_name", data.full_name);
            formDataToSend.append("email", data.email);
            formDataToSend.append("phone", data.phone);
            formDataToSend.append("linkedin", data.linkedin || "");
            formDataToSend.append("cover_letter", data.cover_letter || "");

            if (data.cv_file && data.cv_file[0]) {
                formDataToSend.append("cv_file", data.cv_file[0]);
            }

            const response = await fetch(`${API_BASE_URL}/api/career-applications`, {
                method: "POST",
                body: formDataToSend,
            });

            if (!response.ok) {
                const error = await response.json();

                // Handle duplicate application specifically
                if (response.status === 409) {
                    throw new Error(error.message || "You have already applied for this position.");
                }

                throw new Error(error.message || "Failed to submit application");
            }

            // Store job ID in localStorage to track applications
            const appliedJobs = JSON.parse(localStorage.getItem('appliedJobs') || '[]');
            if (!appliedJobs.includes(job.id)) {
                appliedJobs.push(job.id);
                localStorage.setItem('appliedJobs', JSON.stringify(appliedJobs));
            }

            setSubmitSuccess(true);
            setHasApplied(true);

            // Reset form after 2 seconds and close modal
            setTimeout(() => {
                reset();
                setSubmitSuccess(false);
                onClose();
            }, 2000);

        } catch (error) {
            console.error("Application submission error:", error);
            setSubmitError(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Apply for Position</h2>
                        <p className="text-sm text-gray-600 mt-1">{job?.title}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Success Message */}
                {submitSuccess && (
                    <div className="m-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="font-semibold">Application submitted successfully!</span>
                        </div>
                        <p className="text-sm text-green-700 mt-1">We'll review your application and get back to you soon.</p>
                    </div>
                )}

                {/* Error Message */}
                {submitError && (
                    <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2 text-red-800">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-semibold">Error: {submitError}</span>
                        </div>
                    </div>
                )}

                {/* Already Applied Warning */}
                {hasApplied && !submitSuccess && (
                    <div className="m-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2 text-yellow-800">
                            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                                <span className="font-semibold">Already Applied</span>
                                <p className="text-sm text-yellow-700 mt-1">You have previously applied for this position. Submitting another application may result in your duplicate application being rejected.</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form */}
                {!submitSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="John Doe"
                                    {...register("full_name")}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D4E80]/20 focus:border-[#1D4E80] ${
                                        errors.full_name ? "border-red-500" : "border-gray-300"
                                    }`}
                                />
                                {errors.full_name && (
                                    <p className="text-red-500 text-xs mt-1">{errors.full_name.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Email Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    placeholder="john.doe@example.com"
                                    {...register("email")}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D4E80]/20 focus:border-[#1D4E80] ${
                                        errors.email ? "border-red-500" : "border-gray-300"
                                    }`}
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                                )}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Phone Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    placeholder="+27 12 345 6789"
                                    {...register("phone")}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D4E80]/20 focus:border-[#1D4E80] ${
                                        errors.phone ? "border-red-500" : "border-gray-300"
                                    }`}
                                />
                                {errors.phone && (
                                    <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    LinkedIn Profile (optional)
                                </label>
                                <input
                                    type="url"
                                    placeholder="https://linkedin.com/in/yourprofile"
                                    {...register("linkedin")}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D4E80]/20 focus:border-[#1D4E80] ${
                                        errors.linkedin ? "border-red-500" : "border-gray-300"
                                    }`}
                                />
                                {errors.linkedin && (
                                    <p className="text-red-500 text-xs mt-1">{errors.linkedin.message}</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Upload CV/Resume <span className="text-red-500">*</span>
                            </label>

                            {/* Hidden file input */}
                            <input
                                type="file"
                                id="cv_file_input"
                                accept=".pdf,.doc,.docx"
                                {...register("cv_file")}
                                onChange={handleFileInputChange}
                                className="hidden"
                            />

                            {/* Drag and drop zone */}
                            <div
                                onDragEnter={handleDragEnter}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                                    isDragging
                                        ? "border-[#1D4E80] bg-blue-50"
                                        : errors.cv_file
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                                }`}
                            >
                                {selectedFile ? (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <div className="text-left">
                                                <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                                                <p className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRemoveFile}
                                            className="text-red-500 hover:text-red-700 transition"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <div className="mt-4">
                                            <label
                                                htmlFor="cv_file_input"
                                                className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-[#144E73] transition"
                                            >
                                                Choose file
                                            </label>
                                            <p className="mt-2 text-sm text-gray-600">or drag and drop here</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <p className="text-xs text-gray-500 mt-2">PDF or Word document, max 5MB</p>
                            {errors.cv_file && (
                                <p className="text-red-500 text-xs mt-1">{errors.cv_file.message}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Cover Letter / Message (optional)
                            </label>
                            <textarea
                                placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                                {...register("cover_letter")}
                                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1D4E80]/20 focus:border-[#1D4E80] ${
                                    errors.cover_letter ? "border-red-500" : "border-gray-300"
                                }`}
                                rows={6}
                            />
                            {errors.cover_letter && (
                                <p className="text-red-500 text-xs mt-1">{errors.cover_letter.message}</p>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-[#144E73] transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Submitting..." : "Submit Application"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ApplicationModal;
